/**
 * SCRUM-1010 QC, 27 Sep — telling a top-level area from a sub-area.
 *
 * `/api/area` answers with a flat list: an area and its sub-areas are siblings
 * in the same array, linked by `parentId`. A top-level area is one with no
 * parent — and "no parent" is spelt three ways in the data, because two code
 * paths write it differently:
 *
 * - `null` — every area on Demo Controller D1 (read 28 Sep)
 * - `0` — what `AreaAppService.CreateParentAsync` and the Excel import write
 * - `""` — seen from some list responses
 *
 * So the test is "has no parent id above zero", not an equality against either
 * spelling. `/api/area/parent/{interfaceId}` gets this wrong — it asks for
 * `ParentId.HasValue && ParentId.Value == 0`, which excludes every null-parent
 * area and answered `[]` for D1, leaving the Area dropdowns empty.
 */

export interface AreaRecord {
  id?: number | string
  name?: string
  parentId?: number | string | null
  [key: string]: unknown
}

/** A top-level area: nothing above it. */
export const isTopLevelArea = (area: AreaRecord | undefined | null): boolean =>
  !(Number(area?.parentId ?? 0) > 0)

/**
 * The top-level areas of a flat list, in the shape the dropdowns expect —
 * the same `{ id, name }` the parent endpoint used to return, so its callers
 * need no other change.
 *
 * `Array.isArray`, not `?? []`: some list hooks answer an empty page with
 * `false`, which `??` lets through into `.filter`.
 */
export const parentAreas = (
  list: unknown,
): { id: number | string | undefined; name: string }[] =>
  (Array.isArray(list) ? (list as AreaRecord[]) : [])
    .filter(isTopLevelArea)
    .map((area) => ({ id: area.id, name: String(area.name ?? '') }))
